"use client";

import { useEffect, useEffectEvent, useState } from "react";
import { Interface, isAddress, parseUnits } from "ethers";
import { useConnectWallet, usePrivy, useWallets } from "@privy-io/react-auth";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3000";
const BRIDGE_RETURN_KEY = "agpsira:bridgeReturnPath";
const POLYGON_CHAIN_ID = "0x89";
const USDC_INTERFACE = new Interface([
  "function transfer(address to, uint256 amount) returns (bool)",
]);

type BridgeOnboarding = {
  bridge_customer_id: string;
  customer_type: "individual" | "business";
  kyc_status: string;
  tos_status: string | null;
};

type LinkedBankAccount = {
  bank_name: string | null;
  account_last4: string | null;
  active: boolean;
  liquidation_address: string;
  usdc_contract_address: string;
};

type BridgeBankWithdrawalPanelProps = {
  allowMetaMask?: boolean;
};

type BankForm = {
  fullName: string;
  customerType: "individual" | "business";
  bankName: string;
  accountOwnerName: string;
  checkingOrSavings: "checking" | "savings";
  routingNumber: string;
  accountNumber: string;
  streetLine1: string;
  city: string;
  state: string;
  postalCode: string;
};

const emptyForm: BankForm = {
  fullName: "",
  customerType: "individual",
  bankName: "",
  accountOwnerName: "",
  checkingOrSavings: "checking",
  routingNumber: "",
  accountNumber: "",
  streetLine1: "",
  city: "",
  state: "",
  postalCode: "",
};

function isValidUsRoutingNumber(value: string) {
  const digits = value.replace(/\D/g, "");
  if (!/^\d{9}$/.test(digits)) return false;
  const checksum =
    3 * Number(digits[0] + digits[3] + digits[6]) +
    7 * Number(digits[1] + digits[4] + digits[7]) +
    Number(digits[2] + digits[5] + digits[8]);
  return checksum % 10 === 0;
}

export default function BridgeBankWithdrawalPanel({
  allowMetaMask = false,
}: BridgeBankWithdrawalPanelProps) {
  const { authenticated, getAccessToken, user } = usePrivy();
  const { wallets } = useWallets();
  const { connectWallet } = useConnectWallet();
  const wallet = wallets.find((candidate) => candidate.walletClientType === "privy");
  const metamaskWallet = allowMetaMask
    ? wallets.find((candidate) => candidate.walletClientType === "metamask")
    : undefined;
  const walletAddress = wallet?.address ?? "";
  const email = user?.email?.address ?? "";
  const [isOpen, setIsOpen] = useState(
    () => typeof window !== "undefined" && new URLSearchParams(window.location.search).get("bridge") === "return"
  );
  const [form, setForm] = useState(emptyForm);
  const [onboarding, setOnboarding] = useState<BridgeOnboarding | null>(null);
  const [tosUrl, setTosUrl] = useState<string | null>(null);
  const [kycUrl, setKycUrl] = useState<string | null>(null);
  const [bankAccount, setBankAccount] = useState<LinkedBankAccount | null>(null);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function authorizedFetch(path: string, init: RequestInit = {}) {
    const accessToken = await getAccessToken();
    if (!accessToken) throw new Error("Sign in again to manage bank withdrawals.");
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        ...init.headers,
      },
      cache: "no-store",
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) {
      throw new Error(data.error || `Bank request failed (${response.status}).`);
    }
    return data;
  }

  async function refreshStatus() {
    if (!walletAddress) throw new Error("Privy wallet is not ready.");
    const [onboardingResponse, bankResponse] = await Promise.all([
      authorizedFetch(`/api/customer/${walletAddress}/bridge/onboarding`),
      authorizedFetch(`/api/customer/${walletAddress}/bank-account`),
    ]);
    setOnboarding(onboardingResponse.onboarding);
    setTosUrl(onboardingResponse.tosUrl ?? null);
    setKycUrl(onboardingResponse.kycUrl ?? null);
    setBankAccount(bankResponse.bankAccount);
  }
  const refreshStatusEvent = useEffectEvent(refreshStatus);

  useEffect(() => {
    if (!authenticated || !walletAddress) return;
    let active = true;
    void (async () => {
      setLoading(true);
      try {
        await refreshStatusEvent();
      } catch (loadError) {
        if (active) setError(loadError instanceof Error ? loadError.message : "Could not load Bridge status.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [authenticated, walletAddress]);

  function continueWithBridge(url: string) {
    window.localStorage.setItem(BRIDGE_RETURN_KEY, window.location.pathname);
    window.location.assign(url);
  }

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("bridge") === "return") {
      window.history.replaceState(null, "", window.location.pathname);
      setMessage("Welcome back from Bridge. Your status has been refreshed.");
    }
  }, []);

  function updateForm<K extends keyof BankForm>(field: K, value: BankForm[K]) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function startOnboarding() {
    try {
      setLoading(true);
      setError(null);
      setMessage(null);
      const data = await authorizedFetch(`/api/customer/${walletAddress}/bridge/onboarding`, {
        method: "POST",
        body: JSON.stringify({ fullName: form.fullName, type: form.customerType }),
      });
      setOnboarding(data.onboarding);
      setTosUrl(data.tosUrl ?? null);
      setKycUrl(data.kycUrl ?? null);
      setMessage(data.sandbox ? "Bridge sandbox customer approved for test bank linking." : "Complete Bridge terms and identity checks, then refresh status.");
    } catch (startError) {
      setError(startError instanceof Error ? startError.message : "Could not start Bridge onboarding.");
    } finally {
      setLoading(false);
    }
  }

  async function handleLinkBankAccount(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const routingNumber = form.routingNumber.replace(/\D/g, "");
    const accountNumber = form.accountNumber.replace(/\D/g, "");
    if (!isValidUsRoutingNumber(routingNumber)) {
      setError("Enter a valid 9-digit US routing number.");
      return;
    }
    if (accountNumber.length < 5) {
      setError("Enter a valid bank account number.");
      return;
    }

    try {
      setLinking(true);
      setError(null);
      setMessage(null);
      const data = await authorizedFetch(`/api/customer/${walletAddress}/bank-account`, {
        method: "POST",
        body: JSON.stringify({
          legalName: form.accountOwnerName,
          email,
          routingNumber,
          accountNumber,
          accountOwnerName: form.accountOwnerName,
          bankName: form.bankName,
          checkingOrSavings: form.checkingOrSavings,
          address: {
            streetLine1: form.streetLine1,
            city: form.city,
            state: form.state,
            postalCode: form.postalCode,
            country: "USA",
          },
        }),
      });
      setBankAccount(data.bankAccount);
      setForm(emptyForm);
      setMessage("Bank account linked with Bridge.");
    } catch (linkError) {
      setError(linkError instanceof Error ? linkError.message : "Could not link bank account.");
    } finally {
      setLinking(false);
    }
  }

  async function handleWithdraw() {
    if (!/^\d+(\.\d{1,6})?$/.test(amount) || Number(amount) <= 0) {
      setError("Enter a USDC amount greater than zero.");
      return;
    }
    try {
      setWithdrawing(true);
      setError(null);
      setMessage(null);
      const data = await authorizedFetch(`/api/customer/${walletAddress}/withdraw-to-bank`, {
        method: "POST",
        body: JSON.stringify({ amount }),
      });
      setMessage(`Bridge withdrawal submitted (status: ${data.transfer.status}).`);
    } catch (withdrawError) {
      setError(withdrawError instanceof Error ? withdrawError.message : "Withdrawal failed.");
    } finally {
      setWithdrawing(false);
    }
  }

  async function handleMetaMaskWithdraw() {
    if (!/^[0-9]+(?:\.[0-9]{1,6})?$/.test(amount) || Number(amount) <= 0) {
      setError("Enter a USDC amount greater than zero with up to six decimal places.");
      return;
    }
    if (!metamaskWallet) {
      setError("Connect MetaMask before submitting this payout.");
      return;
    }
    if (!bankAccount?.active || !isAddress(bankAccount.liquidation_address)) {
      setError("The linked Bridge payout address is unavailable.");
      return;
    }
    if (!isAddress(bankAccount.usdc_contract_address)) {
      setError("The Polygon USDC contract address is not configured.");
      return;
    }

    try {
      setWithdrawing(true);
      setError(null);
      setMessage(null);

      let provider = await metamaskWallet.getEthereumProvider();
      let chainId = await provider.request({ method: "eth_chainId" });
      if (String(chainId).toLowerCase() !== POLYGON_CHAIN_ID) {
        await provider.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: POLYGON_CHAIN_ID }],
        });
        provider = await metamaskWallet.getEthereumProvider();
        chainId = await provider.request({ method: "eth_chainId" });
      }
      if (String(chainId).toLowerCase() !== POLYGON_CHAIN_ID) {
        throw new Error("Switch MetaMask to Polygon Mainnet to submit this payout.");
      }

      const data = USDC_INTERFACE.encodeFunctionData("transfer", [
        bankAccount.liquidation_address,
        parseUnits(amount, 6),
      ]);
      const transactionHash = await provider.request({
        method: "eth_sendTransaction",
        params: [
          {
            from: metamaskWallet.address,
            to: bankAccount.usdc_contract_address,
            data,
          },
        ],
      });

      setMessage(`MetaMask USDC transfer submitted to Bridge: ${String(transactionHash)}`);
    } catch (withdrawError) {
      setError(withdrawError instanceof Error ? withdrawError.message : "MetaMask bank withdrawal failed.");
    } finally {
      setWithdrawing(false);
    }
  }

  const onboardingApproved =
    onboarding?.kyc_status === "approved" && onboarding?.tos_status === "approved";

  return (
    <section className="w-full space-y-4 border-t border-slate-800 pt-5">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="inline-flex items-center gap-2 border border-blue-500/40 bg-blue-500/10 px-4 py-3 font-semibold text-blue-200 transition hover:bg-blue-500/20"
      >
        {isOpen ? "Close bank payout" : "Bridge bank payout"}
      </button>

      {isOpen && (
        <div className="max-w-2xl space-y-5">
          {loading && <p className="text-sm text-slate-400">Loading Bridge status...</p>}

          {!loading && !onboarding && (
            <div className="space-y-3">
              <p className="text-sm text-slate-300">Start Bridge terms and identity verification to link your bank.</p>
              <label className="block text-xs text-slate-400" htmlFor="bridge-full-name">Legal name</label>
              <input id="bridge-full-name" value={form.fullName} onChange={(event) => updateForm("fullName", event.target.value)} className="w-full max-w-md border border-slate-700 bg-slate-950 px-3 py-2.5 text-white" autoComplete="name" required />
              <label className="block text-xs text-slate-400" htmlFor="bridge-customer-type">Customer type</label>
              <select id="bridge-customer-type" value={form.customerType} onChange={(event) => updateForm("customerType", event.target.value as BankForm["customerType"])} className="w-full max-w-md border border-slate-700 bg-slate-950 px-3 py-2.5 text-white">
                <option value="individual">Individual</option>
                <option value="business">Business</option>
              </select>
              <button type="button" onClick={() => void startOnboarding()} disabled={loading || !form.fullName.trim()} className="block bg-blue-600 px-4 py-3 font-semibold text-white hover:bg-blue-500 disabled:opacity-50">
                Start Bridge onboarding
              </button>
            </div>
          )}

          {onboarding && !onboardingApproved && (
            <div className="space-y-3">
              <p className="text-sm text-slate-300">Bridge status: {onboarding.kyc_status.replaceAll("_", " ")} · terms {onboarding.tos_status ?? "pending"}</p>
              <p className="text-xs text-slate-400">Bridge (bridge.xyz) is our regulated payment partner. Each step opens a secure Bridge page, then brings you back here.</p>
              <ol className="space-y-2 text-sm text-slate-200">
                <li className="flex flex-wrap items-center gap-3">
                  <span>1. Accept Bridge terms of service — {onboarding.tos_status === "approved" ? "done" : "pending"}</span>
                  {tosUrl && onboarding.tos_status !== "approved" && (
                    <button type="button" onClick={() => continueWithBridge(tosUrl)} className="bg-blue-600 px-3 py-1.5 font-semibold text-white hover:bg-blue-500">Continue with Bridge</button>
                  )}
                </li>
                <li className="flex flex-wrap items-center gap-3">
                  <span>2. Verify your identity with Bridge — {onboarding.kyc_status.replaceAll("_", " ")}</span>
                  {kycUrl && onboarding.tos_status === "approved" && (
                    <button type="button" onClick={() => continueWithBridge(kycUrl)} className="bg-blue-600 px-3 py-1.5 font-semibold text-white hover:bg-blue-500">Continue with Bridge</button>
                  )}
                </li>
              </ol>
              <button type="button" onClick={() => void refreshStatus().catch((refreshError) => setError(refreshError instanceof Error ? refreshError.message : "Status refresh failed."))} className="block border border-slate-700 px-4 py-2 text-sm text-slate-200 hover:border-slate-500">
                Refresh Bridge status
              </button>
            </div>
          )}

          {onboardingApproved && !bankAccount?.active && (
            <form onSubmit={handleLinkBankAccount} className="grid max-w-2xl gap-3 sm:grid-cols-2">
              <label className="text-xs text-slate-400">Bank account owner<input value={form.accountOwnerName} onChange={(event) => updateForm("accountOwnerName", event.target.value)} className="mt-1 w-full border border-slate-700 bg-slate-950 px-3 py-2.5 text-white" required /></label>
              <label className="text-xs text-slate-400">Bank name<input value={form.bankName} onChange={(event) => updateForm("bankName", event.target.value)} className="mt-1 w-full border border-slate-700 bg-slate-950 px-3 py-2.5 text-white" required /></label>
              <label className="text-xs text-slate-400">Account type<select value={form.checkingOrSavings} onChange={(event) => updateForm("checkingOrSavings", event.target.value as BankForm["checkingOrSavings"])} className="mt-1 w-full border border-slate-700 bg-slate-950 px-3 py-2.5 text-white"><option value="checking">Checking</option><option value="savings">Savings</option></select></label>
              <label className="text-xs text-slate-400">Routing number<input inputMode="numeric" autoComplete="off" value={form.routingNumber} onChange={(event) => updateForm("routingNumber", event.target.value)} className="mt-1 w-full border border-slate-700 bg-slate-950 px-3 py-2.5 text-white" maxLength={9} required /></label>
              <label className="text-xs text-slate-400">Account number<input type="password" inputMode="numeric" autoComplete="new-password" value={form.accountNumber} onChange={(event) => updateForm("accountNumber", event.target.value)} className="mt-1 w-full border border-slate-700 bg-slate-950 px-3 py-2.5 text-white" required /></label>
              <label className="text-xs text-slate-400">Street address<input autoComplete="address-line1" value={form.streetLine1} onChange={(event) => updateForm("streetLine1", event.target.value)} className="mt-1 w-full border border-slate-700 bg-slate-950 px-3 py-2.5 text-white" required /></label>
              <label className="text-xs text-slate-400">City<input autoComplete="address-level2" value={form.city} onChange={(event) => updateForm("city", event.target.value)} className="mt-1 w-full border border-slate-700 bg-slate-950 px-3 py-2.5 text-white" required /></label>
              <label className="text-xs text-slate-400">State<input autoComplete="address-level1" value={form.state} onChange={(event) => updateForm("state", event.target.value)} className="mt-1 w-full border border-slate-700 bg-slate-950 px-3 py-2.5 text-white" required /></label>
              <label className="text-xs text-slate-400">ZIP code<input inputMode="numeric" autoComplete="postal-code" value={form.postalCode} onChange={(event) => updateForm("postalCode", event.target.value)} className="mt-1 w-full border border-slate-700 bg-slate-950 px-3 py-2.5 text-white" required /></label>
              <button type="submit" disabled={linking} className="mt-2 w-fit bg-blue-600 px-4 py-3 font-semibold text-white hover:bg-blue-500 disabled:opacity-50">{linking ? "Linking..." : "Link bank account"}</button>
            </form>
          )}

          {onboardingApproved && bankAccount?.active && (
            <div className="max-w-md space-y-3 border-t border-slate-800 pt-4">
              <p className="text-sm text-slate-200">{bankAccount.bank_name} · account ending {bankAccount.account_last4}</p>
              <p className="text-xs text-slate-400">Move USDC from a connected wallet to this bank through Bridge. MetaMask transfers require POL for gas.</p>
              <label htmlFor="bridge-withdraw-amount" className="block text-xs text-slate-400">Amount (USDC)</label>
              <input id="bridge-withdraw-amount" type="number" min="0.000001" step="0.000001" value={amount} onChange={(event) => setAmount(event.target.value)} className="w-full border border-slate-700 bg-slate-950 px-3 py-2.5 text-white" />
              <div className="flex flex-wrap gap-3">
                <button type="button" onClick={() => void handleWithdraw()} disabled={withdrawing} className="bg-emerald-700 px-4 py-3 font-semibold text-white hover:bg-emerald-600 disabled:opacity-50">{withdrawing ? "Submitting..." : allowMetaMask ? "Withdraw from Privy" : "Withdraw to bank"}</button>
                {allowMetaMask && (metamaskWallet ? (
                  <button type="button" onClick={() => void handleMetaMaskWithdraw()} disabled={withdrawing} className="border border-orange-400/60 bg-orange-500/10 px-4 py-3 font-semibold text-orange-200 hover:bg-orange-500/20 disabled:opacity-50">{withdrawing ? "Submitting..." : "Withdraw from MetaMask"}</button>
                ) : (
                  <button type="button" onClick={() => connectWallet({ walletList: ["metamask"] })} disabled={withdrawing} className="border border-orange-400/60 bg-orange-500/10 px-4 py-3 font-semibold text-orange-200 hover:bg-orange-500/20 disabled:opacity-50">Connect MetaMask</button>
                ))}
              </div>
              {allowMetaMask && metamaskWallet && <p className="break-all text-xs text-slate-400">MetaMask: {metamaskWallet.address}</p>}
            </div>
          )}

          {message && <p role="status" className="text-sm text-emerald-300">{message}</p>}
          {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
        </div>
      )}
    </section>
  );
}